import { Injectable, signal, computed } from '@angular/core';
import { ExternalRequestEntry } from '../models/requisitions/interfaces/external-request-entry';
import _ from 'lodash';
import { Quotation } from '../models/order/quotation';
import { S3Object } from '../models/invoice/invoice';

export interface QuotationColumn {
  id: string;
  currency: number;
  exchangeRateToUSD: number;
  providerName: string;
  isProviderSaved: boolean;
  items: ComparativeTableQuotationItem[];
}

export interface ComparativeTableQuotationItem {
  itemCode: string;
  unitPrice: number | null;
  baseTotalPrice: number | null; // Total without taxes (unitPrice * quantity)
  totalPrice: number | null; // Total with taxes
  isLowestPrice: boolean;
  files: S3Object[];
  isTaxExempt: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TableQuotationsComparativeService {
  private productsRequired = signal<ExternalRequestEntry[]>([]);
  private quotationsItems = signal<Quotation[]>([]);
  private lowestPrices = signal<Record<string, number>>({});

  public quotationsColumns = signal<QuotationColumn[]>([]);

  showProductsRequired = computed(() => {
    return this.productsRequired();
  });

  showQuotationsItems = computed(() => {
    return this.quotationsItems();
  });

  constructor() {}

  //#region Getters

  public getQuotationById(id: string): Quotation | undefined {
    return this.quotationsItems().find((q) => q.id === id);
  }

  //#endregion

  //#region Setters
  public ClearAll() {
    this.productsRequired.set([]);
    this.quotationsItems.set([]);
    this.quotationsColumns.set([]);
    this.lowestPrices.set({});
  }

  public SetProductsRequired(products: ExternalRequestEntry[]) {
    if (_.isNil(products)) return;
    this.productsRequired.set(products);
  }

  public PushQuotationsItems(quotations: Quotation[]) {
    if (_.isNil(quotations) || _.isEmpty(quotations)) return;
    quotations.forEach((quotation) => {
      const existingIndex = this.quotationsItems().findIndex((item) => item.id === quotation.id);
      if (existingIndex !== -1) {
        this.quotationsItems()[existingIndex] = quotation;
      } else {
        this.quotationsItems().push(quotation);
      }
    });

    this.populateQuotationColumns();
  }

  //#endregion

  private populateQuotationColumns(): void {
    const products = this.productsRequired();
    const quotations = this.quotationsItems();

    // Step 1: Calculate the lowest price (in USD) for each item across all quotations
    const minPricesUSD: Record<string, number> = {};
    for (const quotation of quotations) {
      for (const item of quotation.items) {
        const totalPrice = this.getTotalPrice(item.unitPrice ?? 0, item.itemCode, item.isTaxExempt);
        const totalPriceUSD = totalPrice / quotation.exchangeRateToUSD;
        const currentMin = minPricesUSD[item.itemCode];
        if (_.isNil(currentMin) || totalPriceUSD < currentMin) {
          minPricesUSD[item.itemCode] = totalPriceUSD;
        }
      }
    }

    // Update the lowestPrices signal with the calculated values
    this.lowestPrices.set(minPricesUSD);

    // Step 2: Build the columns with the correct isLowestPrice flag (comparing in USD)
    const columns: QuotationColumn[] = quotations.map((quotation) => ({
      id: quotation.id,
      currency: quotation.currency,
      exchangeRateToUSD: quotation.exchangeRateToUSD,
      providerName: quotation.providerName ? quotation.providerName! : quotation.unsavedProviderName!,
      isProviderSaved: !_.isNil(quotation.providerReference),
      items: quotation.items.map((item) => {
        const totalPrice = this.getTotalPrice(item.unitPrice ?? 0, item.itemCode, item.isTaxExempt);
        const totalPriceUSD = totalPrice / quotation.exchangeRateToUSD;
        return {
          itemCode: item.itemCode,
          unitPrice: item.unitPrice,
          baseTotalPrice: this.getBaseTotalPrice(item.unitPrice ?? 0, item.itemCode),
          totalPrice,
          isLowestPrice: this.isLowestPriceUSD(item.itemCode, totalPriceUSD, minPricesUSD),
          files: item.files ?? [],
          isTaxExempt: item.isTaxExempt,
        };
      }),
    }));

    // Step 3: Ensure each column has an entry for each product required
    for (const column of columns) {
      for (const product of products) {
        const match = column.items.find((item) => item.itemCode === product.itemCode);
        if (!match) {
          column.items.push({
            itemCode: product.itemCode,
            unitPrice: null,
            baseTotalPrice: null,
            totalPrice: null,
            isLowestPrice: false,
            files: [],
            isTaxExempt: true,
          });
        }
      }

      // Sort the items in the column based on the order of the products required
      column.items.sort((a, b) => {
        const aIndex = products.findIndex((p) => p.itemCode === a.itemCode);
        const bIndex = products.findIndex((p) => p.itemCode === b.itemCode);
        return aIndex - bIndex;
      });
    }

    this.quotationsColumns.set(columns);
  }

  private getBaseTotalPrice(unitPrice: number, itemCode: string): number {
    const product = this.productsRequired().find((p) => p.itemCode === itemCode);
    if (!product) return 0;
    return _.round(unitPrice * product.quantity, 2);
  }

  private getTotalPrice(unitPrice: number, itemCode: string, isTaxExempt: boolean): number {
    const product = this.productsRequired().find((p) => p.itemCode === itemCode);
    if (!product) return 0;
    const TAX_RATE = 0.15;
    const basePrice = unitPrice * product.quantity;
    const totalPrice = isTaxExempt ? basePrice : basePrice * (1 + TAX_RATE);
    return _.round(totalPrice, 2);
  }

  private isLowestPriceUSD(itemCode: string, totalPriceUSD: number, minPricesUSD: Record<string, number>): boolean {
    const lowestPrice = minPricesUSD[itemCode];
    if (_.isNil(lowestPrice)) return false;
    return totalPriceUSD === lowestPrice;
  }

  public removeQuotation(id: string) {
    const updatedQuotations = this.quotationsItems().filter((q) => q.id !== id);
    this.quotationsItems.set(updatedQuotations);
    this.populateQuotationColumns();
  }

  //#region
}
